import { useEffect, useState, useRef } from "react";
import DatePicker from 'react-datepicker';

import filterIcon from "../../../../../Assets/images/filter-icon.svg";
import closeRedIcon from "../../../../../Assets/images/close-red.svg";
import calendarIcon from "../../../../../Assets/images/calender.svg";
import clearIcon from "../../../../../Assets/images/clear-icon.svg";
import searchIcon from "../../../../../Assets/images/search-icon.svg";

import TruckList from "../../Components/TrucksItems/TrucksList/TrucksList";
import AddEditTruckModal from "../../Components/TrucksItems/AddTrucksModal/AddTrucksModal";
import ViewTruckModal from "../../Components/TrucksItems/ViewTruckModal/ViewTruckModal";

import apiClient from "../../../../../Config/apiClient";
import Loader from "../../../../../Context/Loader/Loader";
import { showSuccessToast, showErrorToast } from "../../../../../Context/Toaster/Toaster";
import { DATE_MONTH_FORMAT } from "../../Utils/Constants";
import ConfirmationModal from "../../../../../Components/ConfirmationBox/ConfirmationModal";

const truckPath = process.env.REACT_APP_TC_TRUCK_BASE_PATH;

const Trucks = () => {
    const [trucks, setTrucks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editTruck, setEditTruck] = useState(null);
    const [viewTruck, setViewTruck] = useState(null);
    const [deleteTruck, setDeleteTruck] = useState(null);

    const [filterTruckType, setFilterTruckType] = useState("");
    const [filterStartDate, setFilterStartDate] = useState("");
    const [filterEndDate, setFilterEndDate] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const filterRef = useRef(null);
    const [allTrucks, setAllTrucks] = useState([]);
    const [dateError, setDateError] = useState("");

    const [selectedTruckIds, setSelectedTruckIds] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const fetchTrucks = async (filters = {}) => {
        try {
            setLoading(true);
            const response = await apiClient.post(`${truckPath}/all`, filters);
            if (response.data.status === "SUCCESS") {
                setTrucks(response.data.data);
                setAllTrucks(response.data.data);
            }
        } catch (error) {
            showErrorToast("Failed to fetch trucks");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrucks();
    }, []);

    useEffect(() => {
        if (searchTerm.trim() !== "") {
            const filtered = allTrucks.filter(truck =>
                truck.vehicle_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                truck.permit_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                truck.license_plate_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                truck.insurance_no?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setTrucks(filtered);
        } else {
            setTrucks(allTrucks);
        }
    }, [searchTerm, allTrucks]);

    const validateDates = () => {
        if (filterStartDate && filterEndDate) {
            const start = new Date(filterStartDate);
            const end = new Date(filterEndDate);
            if (end < start) {
                setDateError("End date must be after Start date");
                return false;
            }
        } else if (filterStartDate && !filterEndDate) {
            setDateError("End date is required");
            return false;
        } else if (!filterStartDate && filterEndDate) {
            setDateError("Start date is required");
            return false;
        }
        setDateError("");
        return true;
    };

    const applyFilters = () => {
        if (!validateDates()) return;
        const getUTCDateTime = (dateStr, isEnd = false) => {
            if (!dateStr) return undefined;
            const localDate = new Date(`${dateStr}T${isEnd ? "23:59:59" : "00:00:00"}`);
            return localDate.toISOString();
        };

        const filters = {
            truck_type: filterTruckType || undefined,
            start_date: getUTCDateTime(filterStartDate),
            end_date: getUTCDateTime(filterEndDate, true),
        };

        fetchTrucks(filters);
        setFiltersApplied(
            Boolean(filterTruckType || filterStartDate || filterEndDate || searchTerm)
        );
        setShowFilterDropdown(false);
    };

    useEffect(() => {
        if (searchTerm.trim() !== "") {
            fetchTrucks({ search: searchTerm });
        } else {
            if (filterTruckType || filterStartDate || filterEndDate) {
                applyFilters();
            } else {
                fetchTrucks();
            }
        }
    }, [searchTerm]);

    const clearFilters = () => {
        setFilterTruckType("");
        setFilterStartDate("");
        setFilterEndDate("");
        setSearchTerm("");
        setFiltersApplied(false);
        fetchTrucks();
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setShowFilterDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleAddClick = () => {
        setEditTruck(null);
        setShowModal(true);
    };

    const handleEditClick = async (truck) => {
        try {
            const response = await apiClient.get(`${truckPath}/details/${truck.truck_id}`);
            if (response.data.status === "SUCCESS") {
                setEditTruck(response.data.data);
                setShowModal(true);
            }
        } catch (error) {
            showErrorToast("Failed to fetch truck details");
        }
    };

    const handleViewClick = async (truckId) => {
        console.log('=== handleViewClick called ===');
        console.log('truckId:', truckId);
        console.log('API endpoint:', `${truckPath}/details/${truckId}`);
        try {
            const response = await apiClient.get(`${truckPath}/details/${truckId}`);
            console.log('API response:', response.data);
            if (response.data.status === "SUCCESS") {
                console.log('Setting viewTruck with data:', response.data.data);
                console.log('cvor_doc_url:', response.data.data.cvor_doc_url);
                console.log('permit_doc_url:', response.data.data.permit_doc_url);
                console.log('insurance_doc_url:', response.data.data.insurance_doc_url);
                setViewTruck(response.data.data);
            }
        } catch (error) {
            console.error('API error:', error);
            showErrorToast("Failed to fetch truck details for view");
        }
    };

    const handleSave = async (formData) => {
        const payload = new FormData();

        payload.append("vehicle_no", formData.vehicle_no);
        payload.append("license_plate_no", formData.license_plate_no);
        payload.append("insurance_no", formData.insurance_no);
        payload.append("truck_type", formData.truck_type);
        payload.append("cvor_expiry", formData.cvor_expiry);
        payload.append("insurance_expiry", formData.insurance_expiry);

        if (formData.documents.cvor_doc) payload.append("cvor_doc", formData.documents.cvor_doc);
        if (formData.documents.permit_doc) payload.append("permit_doc", formData.documents.permit_doc);
        if (formData.documents.insurance_doc) payload.append("insurance_doc", formData.documents.insurance_doc);

        // Send explicit removal signal for optional WSIB insurance document only
        if (editTruck && !formData.documents.insurance_doc && !formData.existingDocs?.insurance_doc) {
            payload.append("remove_insurance_doc", "true");
        }

        try {
            if (editTruck) {
                await apiClient.put(`${truckPath}/edit/${formData.truck_id}`, payload, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                showSuccessToast("Truck updated successfully.");
            } else {
                await apiClient.post(`${truckPath}/add`, payload, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                showSuccessToast("Truck added successfully.");
            }

            fetchTrucks();
            setShowModal(false);
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                showErrorToast(err.response.data.message);
            } else {
                showErrorToast("Failed to save truck. Please try again.");
            }
        }
    };

    const handleDeleteClick = (truck) => {
        setDeleteTruck(truck);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTruck) return;

        try {
            const response = await apiClient.delete(`${truckPath}/delete`, {
                data: { truck_ids: [deleteTruck.truck_id] }
            });

            if (response.data.status === "SUCCESS") {
                showSuccessToast(response.data.message || "Truck deleted successfully.");
                fetchTrucks();
            }
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                showErrorToast(err.response.data.message);
            } else {
                showErrorToast("Error deleting truck.");
            }
        } finally {
            setShowDeleteModal(false);
            setDeleteTruck(null);
        }
    };

    return (
        <main className="full-wraper">
            <div className="main-wraper">
                <div className="container-fluid">
                    <div className="main-inner">
                        <div className="common-top">
                            <a onClick={handleAddClick} className="post-button float-end" href="#">
                                {/* <img src={plusIcon} alt="Add Truck" /> */}
                                Add Truck
                            </a>

                            <div className="filter-cvr" ref={filterRef}>
                                <a className="filter-btn" href="#" onClick={(e) => { e.preventDefault(); setShowFilterDropdown((prev) => !prev); }} >
                                    <span className="filter-icon-wrapper">
                                        <img className="ct-icon" src={filterIcon} alt="Filter" />
                                        {filtersApplied && <span className="filter-indicator-dot"></span>}
                                    </span>
                                    Filter
                                </a>

                                <a className="clear-btn" href="#" onClick={(e) => { e.preventDefault(); clearFilters(); }} >
                                    <img className="ct-icon" src={clearIcon} alt="Clear Filter" />
                                    Clear Filter
                                </a>

                                {showFilterDropdown && (
                                    <ul className="dropdown-menu filterdrop-body show">
                                        <div className="filterdrop-split">
                                            <label className="filter-label">
                                                Truck Type
                                                {filterTruckType && (
                                                    <img className="red-close" src={closeRedIcon} alt="clear" onClick={() => setFilterTruckType("")} />
                                                )}
                                            </label>
                                            <select className="filter-select" value={filterTruckType} onChange={(e) => setFilterTruckType(e.target.value)} >
                                                <option value="">--Select--</option>
                                                <option value="Tri-Axle Dump Truck">Tri-Axle Dump Truck</option>
                                            </select>
                                        </div>

                                        <div className="filterdrop-split">
                                            <label className="filter-label">
                                                Created Date
                                                {(filterStartDate || filterEndDate) && (
                                                    <img
                                                        className="red-close"
                                                        src={closeRedIcon}
                                                        alt="Clear"
                                                        onClick={() => { setFilterStartDate(""); setFilterEndDate(""); setDateError(""); }}
                                                    />
                                                )}
                                            </label>
                                            <div className="filter-datepicker-wrapper">
                                                <div className="filter-datepicker from-date">
                                                    <DatePicker
                                                        selected={filterStartDate ? new Date(filterStartDate) : null}
                                                        onChange={(date) => {
                                                            const formatted = date?.toLocaleDateString('en-CA');
                                                            setFilterStartDate(formatted);
                                                            setDateError("");
                                                        }}
                                                        placeholderText="Start Date"
                                                        className="filter-date"
                                                        dateFormat={DATE_MONTH_FORMAT}
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <span className="filter-calendar-icon">
                                                        <img src={calendarIcon} alt="Calendar" />
                                                    </span>
                                                </div>
                                                <div className="filter-datepicker to-date">
                                                    <DatePicker
                                                        selected={filterEndDate ? new Date(filterEndDate) : null}
                                                        onChange={(date) => {
                                                            const formatted = date?.toLocaleDateString('en-CA');
                                                            setFilterEndDate(formatted);
                                                            setDateError("");
                                                        }}
                                                        placeholderText="End Date"
                                                        className="filter-date"
                                                        dateFormat={DATE_MONTH_FORMAT}
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <span className="filter-calendar-icon">
                                                        <img src={calendarIcon} alt="Calendar" />
                                                    </span>
                                                </div>
                                            </div>
                                            {dateError && (
                                                <div className="error-text active" style={{ marginTop: "4px" }}>
                                                    {dateError}
                                                </div>
                                            )}
                                        </div>

                                        <div className="filterdrop-split mb-0">
                                            <div className="filter-buttons">
                                                <a href="#" className="filter-clear" onClick={(e) => { e.preventDefault(); clearFilters(); }}>
                                                    Clear
                                                </a>
                                                <a href="#" className="filter-apply" onClick={(e) => { e.preventDefault(); applyFilters(); }} >
                                                    Apply
                                                </a>
                                            </div>
                                        </div>
                                    </ul>
                                )}
                            </div>

                            <div className="search-bar">
                                <form onSubmit={(e) => { e.preventDefault(); }} >
                                    <input className="form-control me-2" type="search" placeholder="Search Term" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                    <button className="ser-submit" type="submit">
                                        <img src={searchIcon} alt="search" />
                                    </button>
                                </form>
                            </div>
                        </div>

                        {loading ? (
                            <Loader />
                        ) : (
                            <TruckList trucks={trucks} onDelete={handleDeleteClick} onEdit={handleEditClick} onView={handleViewClick} />
                        )}
                    </div>
                </div>
            </div>

            {showModal && (
                <AddEditTruckModal show={showModal} onClose={() => setShowModal(false)} onSave={handleSave} truckData={editTruck} />
            )}

            {viewTruck && (
                <ViewTruckModal truck={viewTruck} onEdit={handleEditClick} onClose={() => setViewTruck(null)} />
            )}

            {showDeleteModal && (
                <ConfirmationModal
                    id="delete-trucks-modal"
                    show={showDeleteModal}
                    title="Confirm Delete"
                    message={`Are you sure you want to delete this truck?`}
                    confirmText="Yes"
                    cancelText="Cancel"
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}

        </main>
    );
};

export default Trucks;